import type { StatusFilter } from '../hooks/useCareRequests';

interface Props {
  statusFilter: StatusFilter;
  onStatusChange: (s: StatusFilter) => void;
  search: string;
  onSearchChange: (v: string) => void;
  totalCount: number;
  filteredCount: number;
  onExportCsv: () => void;
  onRefetch: () => void;
  showDuplicatesOnly: boolean;
  onToggleDuplicates: () => void;
  duplicateCount: number;
}

const STATUS_TABS: StatusFilter[] = ['전체', '접수', '진행중', '완료'];

const STATUS_COLORS: Record<StatusFilter, string> = {
  전체: 'bg-gray-100 text-gray-600',
  접수: 'bg-amber-50 text-amber-600',
  진행중: 'bg-blue-50 text-blue-600',
  완료: 'bg-emerald-50 text-emerald-600',
};

const STATUS_ACTIVE: Record<StatusFilter, string> = {
  전체: 'bg-gray-800 text-white',
  접수: 'bg-amber-500 text-white',
  진행중: 'bg-blue-500 text-white',
  완료: 'bg-emerald-600 text-white',
};

export default function ListToolbar({
  statusFilter,
  onStatusChange,
  search,
  onSearchChange,
  totalCount,
  filteredCount,
  onExportCsv,
  onRefetch,
  showDuplicatesOnly,
  onToggleDuplicates,
  duplicateCount,
}: Props) {
  return (
    <div className="flex flex-col gap-3 mb-5">
      {/* Top row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        {/* Search */}
        <div className="relative flex-1 max-w-xs">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="이름, 접수번호, 연락처 검색..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#2E7D6B] focus:ring-1 focus:ring-[#2E7D6B] bg-white placeholder-gray-300"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 whitespace-nowrap">
            전체 <strong className="text-gray-700">{totalCount}</strong>건 중{' '}
            <strong className="text-gray-700">{filteredCount}</strong>건 표시
          </span>
          <button
            onClick={onRefetch}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:border-[#2E7D6B] hover:text-[#2E7D6B] text-gray-400 transition-colors cursor-pointer"
            title="새로고침"
          >
            <i className="ri-refresh-line text-sm"></i>
          </button>
          <button
            onClick={onExportCsv}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#2E7D6B' }}
          >
            <i className="ri-download-2-line text-sm"></i>
            CSV 내보내기
          </button>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-1.5 flex-wrap items-center">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => onStatusChange(tab)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
              statusFilter === tab ? STATUS_ACTIVE[tab] : STATUS_COLORS[tab]
            }`}
          >
            {tab}
          </button>
        ))}

        <div className="mx-1 w-px h-5 bg-gray-200"></div>

        <button
          onClick={onToggleDuplicates}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-all ${
            showDuplicatesOnly
              ? 'bg-red-500 text-white'
              : 'bg-red-50 text-red-500 hover:bg-red-100'
          }`}
        >
          <i className="ri-error-warning-line mr-1"></i>
          중복 보기{duplicateCount > 0 ? ` (${duplicateCount})` : ''}
        </button>
      </div>
    </div>
  );
}
